import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Users, TrendingUp, Wallet, UserPlus, UserMinus, ShieldCheck, CreditCard, FileUp, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import BrokerAgreementGate from "@/components/BrokerAgreementGate";
import LicenseUploadCard from "@/components/LicenseUploadCard";
import AgencyTrialBanner from "@/components/agency/AgencyTrialBanner";
import AgencyReferralCard from "@/components/agency/AgencyReferralCard";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";
import AgencyLeaderboard from "@/components/AgencyLeaderboard";
import SubAdminsCard from "@/components/SubAdminsCard";
import InstitutionProfileEditor from "@/components/agency/InstitutionProfileEditor";
import AggregatePositionsCard from "@/components/agency/AggregatePositionsCard";
import PlatformLeaderboardCard from "@/components/agency/PlatformLeaderboardCard";
import UserSnapshotDialog from "@/components/admin/UserSnapshotDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import SkeletonGrid from "@/components/SkeletonGrid";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";
import ObondoClientBillingCard from "@/components/agency/ObondoClientBillingCard";
import { useSearchParams } from "react-router-dom";
// Personal trading terminal — retained as an optional collapsible section
// below the aggregate view. Institution admins primarily consume the
// aggregate roll-up (item 7 of Feb 2026 spec) but keep the personal terminal
// as a fallback for testing/education.
import Dashboard from "./Dashboard";

/**
 * AgencyDashboard — now the unified "Obondo Dashboard" for every paying
 * institutional API client (formerly split into separate MoE/CMA-flavored
 * copy — Part 2/B3 retired that distinction; the underlying agency/member
 * data model and `institution_type` field are untouched for compliance
 * history, they just no longer drive the UI).
 *
 * Scope (Phase 1):
 *   - Show agency profile card (name, licence #, seats used / seat limit, status)
 *   - Show aggregate metrics (members, AUM, aggregate PnL, top performers)
 *   - List members with deactivate action
 *   - Invite / create a new member (fills a seat, sets an initial password)
 *   - Section A/B2 self-service Obondo API billing ("API Billing" tab)
 *
 * Broker-only route — gated by `Protected requireBroker` in App.js.
 * Backend contract: routes/agency.py
 */

const fmtKes = (n) =>
    typeof n === "number"
        ? `KES ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
        : "—";

export default function AgencyDashboard() {
    const [searchParams] = useSearchParams();
    const defaultTab = (searchParams.get("stripe_session_id") || searchParams.get("stripe_cancelled")) ? "api-billing" : "traders";
    const [profile, setProfile] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [profileEditOpen, setProfileEditOpen] = useState(false);
    const [snapshotId, setSnapshotId] = useState(null);
    const [showPersonal, setShowPersonal] = useState(false);
    // KPI split-view chip — 'all' | 'employee' | 'external'. Persists across
    // refreshes so admins don't lose their filter on a manual reload.
    const [splitView, setSplitView] = useState(() => {
        try { return sessionStorage.getItem("agency:split-view") || "all"; }
        catch { return "all"; }
    });
    useEffect(() => {
        try { sessionStorage.setItem("agency:split-view", splitView); } catch {}
    }, [splitView]);

    const reload = async () => {
        try {
            const [me, m, list] = await Promise.all([
                api.get("/agency/me").then((r) => r.data),
                api.get("/agency/metrics").then((r) => r.data),
                api.get("/agency/clients").then((r) => r.data),
            ]);
            setProfile(me.agency);
            setMetrics(m);
            setClients(list.clients || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load agency dashboard");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    // Institution vocabulary — CMA institutions use "in-house traders + clients",
    // MoE institutions use "staff + students". Everything else on the dashboard
    // stays identical; only labels differ. See item 7 of the Feb 22 2026 spec.
    const isMoE = profile?.institution_type === "moe";
    const vocab = {
        adminLabel:    isMoE ? "Staff admin"                 : "Broker admin",
        memberLabel:   isMoE ? "Student"                     : "In-house trader / Client",
        memberPlural:  isMoE ? "students"                    : "in-house traders and clients",
        addMember:     isMoE ? "Add student"                 : "Add trader",
        licenseLabel:  isMoE ? "MoE Certificate No."         : "CMA licence",
        agreementDoc:  isMoE ? "Educational Institution Agreement" : "Broker Agreement",
    };

    if (loading) {
        return (
            <div className="p-4 md:p-6" data-testid="agency-loading">
                <SkeletonGrid variant="cards" count={4} />
                <div className="mt-6"><SkeletonGrid variant="rows" count={4} /></div>
            </div>
        );
    }
    if (!profile) return null;

    const usage = (profile.seats_used || 0) / (profile.seat_limit || 100);
    const needsPayment = profile.subscription_status === "pending_payment";
    const needsAgreement = !profile.agreement_acknowledged_at;

    // Split-view derived metrics — the KPI cards for AUM + Total PnL swap to
    // per-slice values based on the chip selection. Employee = in-house
    // (dealing-desk / faculty). External = client / student.
    const viewMetrics = (() => {
        if (splitView === "employee") {
            return {
                aum:     metrics?.aum_kes_employees ?? 0,
                pnl:     metrics?.total_pnl_kes_employees ?? 0,
                subject: isMoE ? "faculty" : "in-house traders",
            };
        }
        if (splitView === "external") {
            return {
                aum:     metrics?.aum_kes_external ?? 0,
                pnl:     metrics?.total_pnl_kes_external ?? 0,
                subject: isMoE ? "students" : "external clients",
            };
        }
        return {
            aum:     metrics?.aum_kes ?? 0,
            pnl:     metrics?.total_pnl_kes ?? 0,
            subject: "all traders",
        };
    })();
    // Filter top performers to match the split-view chip. Legacy rows with no
    // client_type default to "client" — treat them as external for filtering.
    const filteredTop = (metrics?.top_performers || []).filter((p) => {
        if (splitView === "employee") return p.client_type === "employee";
        if (splitView === "external") return p.client_type !== "employee";
        return true;
    });

    const startPayment = () => {
        // Manual bank-transfer flow — brokers upload a bank slip reference via
        // the existing /agency/payment-reference endpoint. This CTA scrolls
        // them to the payment-reference form rendered further down the page.
        const form = document.querySelector('[data-testid="agency-payment-reference-form"]');
        if (form) {
            form.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
            toast.info("Bank transfer instructions live in the Billing tab below.");
        }
    };

    return (
        <div className="p-4 md:p-6 space-y-6" data-testid="agency-dashboard">
            {/* T&C gate — sits above everything until acknowledged */}
            <BrokerAgreementGate open={needsAgreement} onAcknowledged={reload} />

            {/* Pending-payment banner — only during self-serve signup flow */}
            {needsPayment && (
                <div className="border border-primary/40 bg-primary/5 rounded p-4 flex items-center justify-between flex-wrap gap-3"
                     data-testid="agency-pending-payment-banner">
                    <div>
                        <div className="overline text-primary">Activation pending</div>
                        <p className="text-sm mt-1">
                            Complete your KES {(profile.monthly_kes || 50000).toLocaleString()} monthly subscription
                            to unlock client onboarding.
                        </p>
                    </div>
                    <button
                        onClick={startPayment}
                        data-testid="agency-pay-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded font-semibold text-sm"
                    >
                        <CreditCard className="w-4 h-4" />
                        Pay via bank transfer
                    </button>
                </div>
            )}

            {/* --- Header --- */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                        Institutional · {vocab.adminLabel}
                    </div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                        {profile.agency_name}
                    </h1>
                    <div className="mt-1 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                        <ShieldCheck className="w-3.5 h-3.5 text-accent" />
                        <span>{vocab.licenseLabel} · <span className="font-mono">{profile.cma_license_no}</span></span>
                        <span className={"ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold " +
                            (profile.active
                                ? "bg-success/10 text-success"
                                : "bg-danger/10 text-danger")}>
                            {profile.subscription_status?.toUpperCase() || "ACTIVE"}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setProfileEditOpen(true)}
                        data-testid="agency-profile-edit-btn"
                        className="inline-flex items-center gap-1.5 text-xs border border-border rounded px-3 py-2 hover:bg-secondary/40 font-semibold"
                    >
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Edit institution
                    </button>
                    <BulkImportBtn onImported={reload} />
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm shadow-sm hover:opacity-90 transition-opacity"
                        onClick={() => setInviteOpen(true)}
                        data-testid="agency-invite-btn"
                    >
                        <UserPlus className="w-4 h-4" />
                        {vocab.addMember}
                    </button>
                </div>
            </div>

            {/* Seat Pack expansion banner — shown when 80%+ seats used */}
            {usage >= 0.8 && !needsPayment && (
                <div className="border border-primary/40 bg-primary/5 rounded p-4 flex items-center justify-between flex-wrap gap-3"
                     data-testid="seat-pack-banner">
                    <div>
                        <div className="overline text-primary flex items-center gap-2">
                            <Rocket className="w-3.5 h-3.5" /> Seat Pack available
                        </div>
                        <p className="text-sm mt-1">
                            You're at {Math.round(usage * 100)}% seat capacity. Add <strong>+50 seats</strong> for KES 20,000/month
                            to keep onboarding without pause.
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            toast.info("Seat-pack expansion (+50 seats · KES 20k/mo) now goes through the manual bank-transfer flow. Contact your admin — they'll issue a payment reference and apply the seats once the transfer clears.");
                        }}
                        data-testid="seat-pack-buy-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded font-semibold text-sm"
                    >
                        <CreditCard className="w-4 h-4" />
                        Buy +50 seats
                    </button>
                </div>
            )}

            {/* --- KPI row + split-view chip --- */}
            <SplitViewControls
                isMoE={isMoE}
                view={splitView}
                onChange={setSplitView}
                counts={{
                    all:      (metrics?.employees_count ?? 0) + (metrics?.external_clients_count ?? 0),
                    employee:  metrics?.employees_count ?? 0,
                    external:  metrics?.external_clients_count ?? 0,
                }}
            />
            {/* 7-day trial + annual plan lifecycle banner */}
            <AgencyTrialBanner agency={profile} onReload={reload} />

            {/* Unified layout engine — KPIs anchor top-left (Directive 13),
                every block drags / resizes / minimizes / removes. */}
            <DashboardGridWrapper
                role="SUB_ADMIN"
                viewId="agency-home"
                widgets={[
                    {
                        id: "kpi-seats", title: "Seats Used", category: "KPIs", layout: { x: 0, y: 0, w: 3, h: 3 }, minW: 3, minH: 2, noPadding: true,
                        render: () => <KpiCard icon={<Users />} label="Seats used" value={`${profile.seats_used || 0} / ${profile.seat_limit || 100}`}
                            subtext={`${Math.round(usage * 100)}% utilised`} tone={usage > 0.9 ? "danger" : usage > 0.7 ? "warn" : "ok"}
                            testId="kpi-seats" />,
                    },
                    {
                        id: "kpi-split", title: isMoE ? "Staff / Students" : "Traders / Clients", category: "KPIs", layout: { x: 3, y: 0, w: 3, h: 3 }, minW: 3, minH: 2, noPadding: true,
                        render: () => <KpiCard icon={<Users />} label={isMoE ? "Staff / Students" : "In-house traders / Clients"}
                            value={`${metrics?.employees_count ?? 0} / ${metrics?.external_clients_count ?? 0}`}
                            subtext={isMoE ? "Faculty vs students" : "Dealing-desk vs external"}
                            tone="ok" testId="kpi-split" />,
                    },
                    {
                        id: "kpi-aum", title: "AUM (Virtual)", category: "KPIs", layout: { x: 6, y: 0, w: 3, h: 3 }, minW: 3, minH: 2, noPadding: true,
                        render: () => <KpiCard icon={<Wallet />} label={splitLabel(splitView, isMoE, "AUM (virtual)")}
                            value={fmtKes(viewMetrics.aum)}
                            subtext="Sum of virtual balances" tone="ok" testId="kpi-aum" />,
                    },
                    {
                        id: "kpi-pnl", title: "Total PnL", category: "KPIs", layout: { x: 9, y: 0, w: 3, h: 3 }, minW: 3, minH: 2, noPadding: true,
                        render: () => <KpiCard icon={<TrendingUp />} label={splitLabel(splitView, isMoE, "Total PnL")}
                            value={fmtKes(viewMetrics.pnl)}
                            subtext={`Across ${viewMetrics.subject}`}
                            tone={signTone(viewMetrics.pnl || 0)} testId="kpi-pnl" />,
                    },
                    {
                        id: "agency-referral", title: "Referral Programme", category: "Growth", layout: { x: 0, y: 3, w: 6, h: 6 }, minW: 3, minH: 4, noPadding: true,
                        render: () => <AgencyReferralCard />,
                    },
                    {
                        id: "agency-license", title: "Compliance & Licence", category: "Compliance", layout: { x: 6, y: 3, w: 6, h: 6 }, minW: 3, minH: 4, noPadding: true,
                        render: () => <LicenseUploadCard agency={profile} onUploaded={reload} />,
                    },
                    {
                        id: "aggregate-positions", title: "Aggregate Positions", category: "Portfolio", layout: { x: 0, y: 9, w: 12, h: 7 }, minW: 6, minH: 4, noPadding: true,
                        render: () => <AggregatePositionsCard vocab={vocab} isMoE={isMoE} />,
                    },
                ]}
            />

            {/* --- Below-the-fold: TABS for scannability --- */}
            <Tabs defaultValue={defaultTab} className="mt-2">
                <TabsList className="w-full justify-start border-b border-border bg-transparent p-0 rounded-none h-auto overflow-x-auto">
                    <TabsTrigger value="traders"      data-testid="agency-tab-traders"      className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent whitespace-nowrap">{isMoE ? "Staff & Students" : "Traders & Clients"}</TabsTrigger>
                    <TabsTrigger value="leaderboards" data-testid="agency-tab-leaderboards" className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent whitespace-nowrap">Leaderboards</TabsTrigger>
                    <TabsTrigger value="admins"       data-testid="agency-tab-admins"       className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent whitespace-nowrap">Sub-admins</TabsTrigger>
                    <TabsTrigger value="compliance"   data-testid="agency-tab-compliance"   className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent whitespace-nowrap">Compliance</TabsTrigger>
                    <TabsTrigger value="performers"   data-testid="agency-tab-performers"   className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent whitespace-nowrap">Top performers</TabsTrigger>
                    <TabsTrigger value="settings"     data-testid="agency-tab-settings"     className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent whitespace-nowrap">Settings</TabsTrigger>
                    <TabsTrigger value="api-billing"  data-testid="agency-tab-api-billing"  className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent whitespace-nowrap">API Billing</TabsTrigger>
                </TabsList>

                <TabsContent value="traders" className="mt-4">
                    <ClientTable
                        clients={clients}
                        vocab={vocab}
                        isMoE={isMoE}
                        onSnapshot={(id) => setSnapshotId(id)}
                        onPause={async (id) => {
                            if (!window.confirm("Pause this trader's account? They lose access until you unpause.")) return;
                            try { await api.post(`/agency/clients/${id}/pause`); toast.success("Paused"); reload(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Pause failed"); }
                        }}
                        onUnpause={async (id) => {
                            try { await api.post(`/agency/clients/${id}/unpause`); toast.success("Reactivated"); reload(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Unpause failed"); }
                        }}
                        onDelete={async (id) => {
                            if (!window.confirm("Delete this trader? They lose all access. This is reversible via Restore.")) return;
                            try { await api.post(`/agency/clients/${id}/soft-delete`); toast.success("Deleted"); reload(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                        }}
                        onRestore={async (id) => {
                            try { await api.post(`/agency/clients/${id}/restore`); toast.success("Restored"); reload(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
                        }}
                        onDeactivate={async (id) => {
                            if (!window.confirm("Deactivate this client? They lose platform access and their seat is freed.")) return;
                            try { await api.delete(`/agency/clients/${id}`); toast.success("Client deactivated"); reload(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Deactivation failed"); }
                        }}
                    />
                </TabsContent>

                <TabsContent value="leaderboards" className="mt-4">
                    <div className="grid md:grid-cols-2 gap-4">
                        <AgencyLeaderboard />
                        <PlatformLeaderboardCard />
                    </div>
                </TabsContent>

                <TabsContent value="admins" className="mt-4">
                    <SubAdminsCard />
                </TabsContent>

                <TabsContent value="performers" className="mt-4">
                    {filteredTop.length > 0 ? (
                        <div className="border border-border bg-card rounded-xl p-4">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                                Top performers
                                {splitView !== "all" && (
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/30 rounded-full px-2 py-0.5">
                                        {splitView === "employee"
                                            ? (isMoE ? "Faculty" : "In-house")
                                            : (isMoE ? "Students" : "External clients")}
                                        {" only"}
                                    </span>
                                )}
                            </div>
                            <ul className="divide-y divide-border" data-testid="agency-top-performers">
                                {filteredTop.map((p, i) => (
                                    <li key={p.name + i} className="flex justify-between items-center py-2 text-sm">
                                        <span className="flex items-center gap-2">
                                            <span className="w-6 h-6 rounded-full bg-secondary text-muted-foreground flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                                            {p.name}
                                        </span>
                                        <span className={"font-mono proportional-nums font-semibold " + toneClassName(signTone(p.pnl))}>
                                            {signedPrefix(p.pnl)}{fmtKes(p.pnl)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <div className="border border-border bg-card rounded-xl p-8 text-center text-sm text-muted-foreground">
                            No performers yet — invite your traders to start.
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="compliance" className="mt-4">
                    <AgencyComplianceTab agencyId={profile?.id || profile?.agency_id} vocab={vocab} />
                </TabsContent>

                <TabsContent value="settings" className="mt-4 space-y-4">
                    <AgencyReferralCard />
                </TabsContent>

                <TabsContent value="api-billing" className="mt-4">
                    <ObondoClientBillingCard />
                </TabsContent>
            </Tabs>

            {/* Snapshot dialog (incognito view of one client) */}
            <UserSnapshotDialog
                userId={snapshotId}
                open={!!snapshotId}
                onClose={() => setSnapshotId(null)}
                apiPath={(id) => `/agency/clients/${id}/snapshot`}
            />

            {inviteOpen && (
                <InviteClientModal
                    onClose={() => setInviteOpen(false)}
                    onCreated={() => { setInviteOpen(false); reload(); }}
                />
            )}

            <InstitutionProfileEditor
                open={profileEditOpen}
                agency={profile}
                onClose={() => setProfileEditOpen(false)}
                onSaved={reload}
            />

            {/* ────────────────────────────────────────────────────────────
                Personal trading terminal — OPTIONAL for institution admins.
                Aggregate view above is primary; admins can toggle their own
                sandbox terminal on for testing/demo.
               ──────────────────────────────────────────────────────────── */}
            <div className="pt-8 mt-8 border-t-2 border-dashed border-border" data-testid="agency-personal-terminal">
                <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">
                            Your personal trading terminal
                        </div>
                        <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                            My trading account
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Admins don't typically trade — your traders do. Open this if you want a personal sandbox
                            to test features or demo the terminal to your team.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowPersonal((s) => !s)}
                        data-testid="agency-toggle-personal-terminal"
                        className="text-xs border border-border rounded px-3 py-2 hover:bg-secondary/40 font-semibold"
                    >
                        {showPersonal ? "Hide my terminal" : "Show my terminal"}
                    </button>
                </div>
                {showPersonal && <Dashboard />}
            </div>
        </div>
    );
}

/* -------- Small helpers -------- */

function KpiCard({ icon, label, value, subtext, tone, testId }) {
    const toneCls =
        tone === "danger" || tone === "neg" ? "text-danger" :
        tone === "ok" || tone === "pos"     ? "text-success" :
        tone === "zero"                     ? "text-zero" :
        tone === "warn"                     ? "text-primary" :
                                              "text-accent";
    return (
        <div className="border border-border bg-card rounded p-3 min-w-0" data-testid={testId}>
            <div className={"flex items-center gap-2 text-xs uppercase tracking-widest " + toneCls}>
                <span className="[&>svg]:w-3.5 [&>svg]:h-3.5 shrink-0">{icon}</span>
                <span className="truncate">{label}</span>
            </div>
            <div className="mt-1 font-extrabold text-xl md:text-2xl whitespace-nowrap">{value}</div>
            {subtext && <div className="text-[10px] text-muted-foreground mt-0.5 break-words">{subtext}</div>}
        </div>
    );
}

function ClientTable({ clients, onDeactivate, onPause, onUnpause, onDelete, onRestore, onSnapshot, vocab, isMoE }) {
    const [filter, setFilter] = useState("all");
    const employees = clients.filter((c) => c.client_type === "employee");
    const externals = clients.filter((c) => c.client_type !== "employee");
    const shown =
        filter === "employees" ? employees :
        filter === "clients"   ? externals :
                                 clients;
    const { sorted: sortedShown, sortKey, direction, requestSort } = useSortableData(shown, {
        accessors: {
            name:   (c) => c.full_name || "",
            type:   (c) => c.client_type || "",
            email:  (c) => c.email || "",
            phone:  (c) => c.phone || "",
            status: (c) => c.account_status || "active",
        },
    });
    const empLabel = isMoE ? "Staff" : "In-house traders";
    const extLabel = isMoE ? "Students" : "Clients";
    return (
        <div className="border border-border bg-card rounded overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
                <div className="overline">{isMoE ? "Staff & Students" : "In-house Traders & Clients"} · {clients.length}</div>
                <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                    <FilterTab active={filter === "all"} onClick={() => setFilter("all")}
                        label={`All · ${clients.length}`} testId="filter-all" />
                    <FilterTab active={filter === "employees"} onClick={() => setFilter("employees")}
                        label={`${empLabel} · ${employees.length}`} testId="filter-employees" />
                    <FilterTab active={filter === "clients"} onClick={() => setFilter("clients")}
                        label={`${extLabel} · ${externals.length}`} testId="filter-clients" />
                </div>
            </div>
            {shown.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground" data-testid="agency-clients-empty">
                    {filter === "all"
                        ? `No ${vocab?.memberPlural || "traders"} yet. Tap "${vocab?.addMember || "Add"}" to onboard your first.`
                        : `No ${filter} yet in this filter.`}
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40 text-xs uppercase tracking-widest text-muted-foreground">
                        <tr>
                            <SortableTh label="Name"   sortKey="name"   currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Type"   sortKey="type"   currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Email"  sortKey="email"  currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Phone"  sortKey="phone"  currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Status" sortKey="status" currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <th className="text-right px-4 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody data-testid="agency-clients-table">
                        {sortedShown.map((c) => {
                            const st = c.account_status || "active";
                            const isDeleted = st === "deleted";
                            const isPaused  = st === "paused";
                            const isDeact   = st === "deactivated";
                            return (
                                <tr key={c.id} className="border-t border-border">
                                    <td className="px-4 py-2">{c.full_name}</td>
                                    <td className="px-4 py-2"><TypeBadge type={c.client_type} /></td>
                                    <td className="px-4 py-2 text-muted-foreground break-all">{c.email}</td>
                                    <td className="px-4 py-2 mono">{c.phone}</td>
                                    <td className="px-4 py-2">
                                        <StatusPill status={st} />
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        <div className="inline-flex items-center gap-3 flex-wrap justify-end">
                                            <button
                                                onClick={() => onSnapshot(c.id)}
                                                data-testid={`agency-snapshot-${c.id}`}
                                                className="text-xs text-muted-foreground hover:text-primary"
                                                title="Incognito view — see this trader's dashboard"
                                            >
                                                Incognito
                                            </button>
                                            {isPaused || isDeact ? (
                                                <button
                                                    onClick={() => onUnpause(c.id)}
                                                    data-testid={`agency-unpause-${c.id}`}
                                                    className="text-xs text-success hover:underline"
                                                    title="Reactivate this trader"
                                                >Unpause</button>
                                            ) : isDeleted ? null : (
                                                <button
                                                    onClick={() => onPause(c.id)}
                                                    data-testid={`agency-pause-${c.id}`}
                                                    className="text-xs text-amber-600 hover:underline"
                                                    title="Pause this trader"
                                                >Pause</button>
                                            )}
                                            {isDeleted ? (
                                                <button
                                                    onClick={() => onRestore(c.id)}
                                                    data-testid={`agency-restore-${c.id}`}
                                                    className="text-xs text-success hover:underline"
                                                    title="Restore this soft-deleted trader"
                                                >Restore</button>
                                            ) : (
                                                <button
                                                    onClick={() => onDelete(c.id)}
                                                    data-testid={`agency-delete-${c.id}`}
                                                    className="text-xs text-danger hover:underline"
                                                    title="Soft-delete this trader (reversible)"
                                                >Delete</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function FilterTab({ active, onClick, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={
                "px-3 py-1.5 font-semibold border-r border-border last:border-r-0 " +
                (active
                    ? "bg-primary text-primary-foreground"
                    : "bg-transparent hover:bg-secondary/40 text-muted-foreground")
            }
        >
            {label}
        </button>
    );
}

function TypeBadge({ type }) {
    const isEmp = type === "employee";
    return (
        <span className={"px-2 py-0.5 rounded text-[10px] font-bold tracking-wide " + (
            isEmp
                ? "bg-accent/10 text-accent border border-accent/30"
                : "bg-secondary text-muted-foreground border border-border"
        )}>
            {isEmp ? "EMPLOYEE" : "CLIENT"}
        </span>
    );
}

function StatusPill({ status }) {
    const map = {
        active:        { cls: "bg-success/10 text-success",  label: "ACTIVE" },
        frozen:        { cls: "bg-danger/10 text-danger",    label: "FROZEN" },
        deactivated:   { cls: "bg-muted text-muted-foreground", label: "DEACTIVATED" },
    };
    const s = map[status] || map.active;
    return <span className={"px-2 py-0.5 rounded-full text-[10px] font-bold " + s.cls}>{s.label}</span>;
}

function InviteClientModal({ onClose, onCreated }) {
    const [mode, setMode] = useState("temp");   // 'temp' (current) or 'email' (send invite link)
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "", initial_password: "", client_type: "client",
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (mode === "email") {
                await api.post("/agency/clients/invite", {
                    full_name: form.full_name, email: form.email, client_type: form.client_type,
                });
                toast.success(`Invite sent to ${form.email}`);
            } else {
                await api.post("/agency/clients", form);
                toast.success(`${form.full_name} onboarded successfully`);
            }
            onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Onboarding failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
             onClick={onClose} data-testid="agency-invite-modal">
            <form
                className="w-full max-w-md bg-card border border-border rounded-lg shadow-xl p-5"
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
            >
                <h2 className="text-xl font-bold mb-1">Onboard a trader</h2>
                <p className="text-xs text-muted-foreground mb-4">
                    Full institutional access. No billing — covered by your Institutional plan.
                </p>

                {/* Mode toggle — email invite vs manual temp password */}
                <div className="mb-3">
                    <span className="block text-xs font-semibold text-muted-foreground mb-1">
                        Onboarding method
                    </span>
                    <div className="grid grid-cols-2 gap-2" role="radiogroup">
                        <TypeToggle
                            active={mode === "email"}
                            onClick={() => setMode("email")}
                            label="Send email invite"
                            subtext="Recommended · they set their own password"
                            testId="invite-mode-email"
                        />
                        <TypeToggle
                            active={mode === "temp"}
                            onClick={() => setMode("temp")}
                            label="Set temporary password"
                            subtext="You share it out-of-band"
                            testId="invite-mode-temp"
                        />
                    </div>
                </div>

                {/* Employee vs Client toggle */}
                <div className="mb-3">
                    <span className="block text-xs font-semibold text-muted-foreground mb-1">
                        Trader type
                    </span>
                    <div className="grid grid-cols-2 gap-2" role="radiogroup">
                        <TypeToggle
                            active={form.client_type === "employee"}
                            onClick={() => setForm((f) => ({ ...f, client_type: "employee" }))}
                            label="In-house Employee"
                            subtext="Own team in training"
                            testId="agency-invite-type-employee"
                        />
                        <TypeToggle
                            active={form.client_type === "client"}
                            onClick={() => setForm((f) => ({ ...f, client_type: "client" }))}
                            label="External Client"
                            subtext="Retail client of the brokerage"
                            testId="agency-invite-type-client"
                        />
                    </div>
                </div>

                <div className="space-y-3">
                    <Field label="Full name" value={form.full_name} testId="agency-invite-name"
                        onChange={(v) => setForm((f) => ({ ...f, full_name: v }))} />
                    <Field label="Email" type="email" value={form.email} testId="agency-invite-email"
                        onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
                    {mode === "temp" && (
                        <>
                            <Field label="Phone (+254…)" value={form.phone} testId="agency-invite-phone"
                                onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
                            <Field label="Initial password (they rotate on first login)"
                                type="password" value={form.initial_password} testId="agency-invite-password"
                                onChange={(v) => setForm((f) => ({ ...f, initial_password: v }))} />
                        </>
                    )}
                    {mode === "email" && (
                        <p className="text-[11px] text-muted-foreground border border-dashed border-border rounded p-2">
                            Invite link expires in 14 days. Trader receives an email with a one-time
                            activation URL where they set their own phone number and password.
                        </p>
                    )}
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" className="px-3 py-1.5 text-sm border border-border rounded"
                            onClick={onClose} data-testid="agency-invite-cancel">Cancel</button>
                    <button type="submit" className="px-4 py-1.5 text-sm bg-primary text-primary-foreground font-semibold rounded"
                            disabled={busy} data-testid="agency-invite-submit">
                        {busy ? "Working…" : mode === "email" ? "Send invite" : "Onboard"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function BulkImportBtn({ onImported }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/agency/clients/bulk", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(r.data);
            toast.success(`Imported ${r.data.created_count} · ${r.data.failed_count} failed`);
            onImported?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk import failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={onFile}
                data-testid="bulk-import-input"
            />
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                data-testid="bulk-import-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border text-xs font-semibold hover:bg-secondary/40 disabled:opacity-60"
                title="Required CSV headers: full_name,email,phone,client_type"
            >
                <FileUp className="w-3.5 h-3.5" />
                {busy ? "Importing…" : "Bulk CSV"}
            </button>
            {result && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                     onClick={() => setResult(null)} data-testid="bulk-import-result-modal">
                    <div className="w-full max-w-lg bg-card border border-border rounded-lg p-5 max-h-[80vh] overflow-y-auto"
                         onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-bold mb-2">
                            Bulk import result — {result.created_count} imported / {result.failed_count} failed
                        </h3>
                        {result.failed_count > 0 && (
                            <>
                                <div className="overline text-danger mt-3">Failed rows</div>
                                <ul className="text-xs space-y-1 mt-1">
                                    {result.failed.slice(0, 20).map((f) => (
                                        <li key={`row-${f.row}-${f.email || "noemail"}`} className="border-l-2 border-danger/50 pl-2">
                                            Row {f.row} · {f.email || "—"} — <em>{f.reason}</em>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                        {result.created_count > 0 && (
                            <>
                                <div className="overline text-success mt-3">Temporary passwords (share out-of-band)</div>
                                <ul className="text-xs space-y-1 mt-1 font-mono">
                                    {result.created.slice(0, 20).map((c) => (
                                        <li key={c.email}>
                                            {c.email} · <span className="text-primary">{c.temp_password}</span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                        <button
                            onClick={() => setResult(null)}
                            className="mt-4 w-full py-2 bg-primary text-primary-foreground rounded font-semibold text-sm"
                            data-testid="bulk-import-result-close"
                        >Close</button>
                    </div>
                </div>
            )}
        </>
    );
}

function TypeToggle({ active, onClick, label, subtext, testId }) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={active}
            onClick={onClick}
            data-testid={testId}
            className={
                "text-left rounded border p-2 transition-colors " +
                (active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-secondary/20 hover:border-primary/50")
            }
        >
            <div className="text-xs font-bold">{label}</div>
            <div className="text-[10px] text-muted-foreground">{subtext}</div>
        </button>
    );
}

function Field({ label, value, onChange, type = "text", testId }) {
    return (
        <label className="block">
            <span className="block text-xs font-semibold text-muted-foreground mb-1">{label}</span>
            <input
                type={type}
                value={value}
                required
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded focus:border-primary focus:outline-none"
            />
        </label>
    );
}


/** Segmented control that filters the AUM + PnL KPI cards to All / In-house / External. */
function SplitViewControls({ view, onChange, counts, isMoE }) {
    // MoE variant relabels but keeps the same underlying `client_type` mapping.
    const empLabel = isMoE ? "Faculty" : "In-house";
    const extLabel = isMoE ? "Students" : "Clients";
    const opts = [
        { k: "all",      label: "All",     n: counts.all,      testId: "split-view-all" },
        { k: "employee", label: empLabel,  n: counts.employee, testId: "split-view-employee" },
        { k: "external", label: extLabel,  n: counts.external, testId: "split-view-external" },
    ];
    return (
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="split-view-chip">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Split view · aggregate by segment
            </div>
            <div className="inline-flex rounded-full border border-border overflow-hidden text-xs bg-card">
                {opts.map((o) => (
                    <button
                        key={o.k}
                        type="button"
                        onClick={() => onChange(o.k)}
                        data-testid={o.testId}
                        aria-pressed={view === o.k}
                        className={`px-3 py-1 font-bold transition ${
                            view === o.k
                                ? "bg-primary text-primary-foreground"
                                : "text-foreground hover:bg-muted"
                        }`}
                    >
                        {o.label}{" "}
                        <span className={`text-[10px] font-mono ${view === o.k ? "opacity-80" : "text-muted-foreground"}`}>
                            · {o.n}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

/** Build the AUM / PnL KPI card label based on the current split-view. */
function splitLabel(view, isMoE, baseLabel) {
    if (view === "employee") return `${isMoE ? "Faculty" : "In-house"} · ${baseLabel}`;
    if (view === "external") return `${isMoE ? "Students" : "Clients"} · ${baseLabel}`;
    return baseLabel === "AUM (virtual)" ? "Combined AUM (virtual)" : baseLabel;
}


/**
 * AgencyComplianceTab — Sub-Admin view of the Investment Strategy compliance
 * status for members of a single agency. Hits GET /agency/{id}/investment-strategies.
 * Sub-admins can view + download members' PDFs; only Super Admin can edit.
 */
const _CAT_LABELS = {
    conservative:   "Capital Preservation",
    below_average:  "Income w/ Growth",
    average:        "Balanced",
    above_average:  "Growth w/ Income",
    aggressive:     "Growth (Aggressive)",
};
const _CAT_COLOR = {
    conservative:   "bg-blue-500/15 text-blue-500 border-blue-500/40",
    below_average:  "bg-teal-500/15 text-teal-500 border-teal-500/40",
    average:        "bg-primary/15 text-primary border-primary/40",
    above_average:  "bg-amber-500/15 text-amber-500 border-amber-500/40",
    aggressive:     "bg-danger/15 text-danger border-danger/40",
};

function AgencyComplianceTab({ agencyId, vocab }) {
    const [rows, setRows]     = useState(null);
    const [filter, setFilter] = useState("all"); // all / completed / pending

    useEffect(() => {
        if (!agencyId) return;
        setRows(null);
        api.get(`/agency/${agencyId}/investment-strategies`)
            .then(({ data }) => setRows(data.rows || []))
            .catch(() => setRows([]));
    }, [agencyId]);

    const shown = (rows || []).filter((r) => {
        if (filter === "completed") return r.completed;
        if (filter === "pending")   return !r.completed;
        return true;
    });
    const { sorted: sortedShown, sortKey, direction, requestSort } = useSortableData(shown, {
        accessors: {
            member:   (r) => r.full_name || r.email || "",
            status:   (r) => (r.completed ? 1 : 0),
            category: (r) => r.category || "",
            score:    (r) => Number(r.score ?? -Infinity),
        },
    });
    if (!agencyId) {
        return <div className="text-sm text-muted-foreground">Loading agency…</div>;
    }
    const totals = (rows || []).reduce(
        (t, r) => ({
            total:     t.total + 1,
            completed: t.completed + (r.completed ? 1 : 0),
        }),
        { total: 0, completed: 0 },
    );
    const rate = totals.total > 0 ? Math.round((totals.completed / totals.total) * 100) : 0;

    async function downloadPdf(strategyId, email) {
        try {
            const r = await api.get(`/investment-strategy/pdf/${strategyId}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${email || "member"}-investment-strategy.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Could not open PDF"); }
    }

    return (
        <div className="space-y-4" data-testid="agency-compliance-tab">
            {/* KPIs */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border-2 border-border bg-card p-4" data-testid="ac-kpi-total">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{vocab?.member_plural || "Members"}</div>
                    <div className="text-3xl font-extrabold mt-1 mono">{totals.total}</div>
                </div>
                <div className="rounded-xl border-2 border-border bg-card p-4" data-testid="ac-kpi-completed">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Completed</div>
                    <div className="text-3xl font-extrabold mt-1 mono text-emerald-500">{totals.completed}</div>
                </div>
                <div className="rounded-xl border-2 border-border bg-card p-4" data-testid="ac-kpi-rate">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Completion %</div>
                    <div className="text-3xl font-extrabold mt-1 mono text-primary">{rate}%</div>
                </div>
            </div>

            {/* Filter chips */}
            <div className="flex items-center gap-1.5">
                {["all", "completed", "pending"].map((k) => (
                    <button
                        key={k}
                        onClick={() => setFilter(k)}
                        data-testid={`ac-filter-${k}`}
                        className={
                            "px-3 h-7 rounded text-[11px] font-bold uppercase tracking-wide border transition-colors " +
                            (filter === k ? "bg-primary/20 border-primary text-primary" : "border-border text-muted-foreground")
                        }
                    >{k}</button>
                ))}
                <div className="ml-auto text-[10px] text-muted-foreground">
                    {shown.length} shown · read-only view · contact Super Admin to change status
                </div>
            </div>

            {/* Table */}
            <div className="rounded-xl border-2 border-border bg-card overflow-x-auto" data-testid="ac-table">
                <table className="w-full text-sm min-w-[560px]">
                    <thead className="bg-card border-b border-border">
                        <tr>
                            <SortableTh label="Member"   sortKey="member"   currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                            <SortableTh label="Status"   sortKey="status"   currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                            <SortableTh label="Category" sortKey="category" currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                            <SortableTh label="Score"    sortKey="score"    currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="text-xs uppercase tracking-widest" />
                            <th className="text-right px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground">PDF</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows === null ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>
                        ) : shown.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                                No {filter === "all" ? "members" : `${filter} members`} in this agency yet.
                            </td></tr>
                        ) : sortedShown.map((r) => (
                            <tr key={r.user_id} className="border-t border-border" data-testid={`ac-row-${r.user_id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-semibold">{r.full_name || "—"}</div>
                                    <div className="text-xs text-muted-foreground break-all">{r.email}</div>
                                </td>
                                <td className="px-4 py-3 text-xs font-semibold">
                                    {r.completed
                                        ? <span className="text-emerald-500">Completed</span>
                                        : <span className="text-danger">Pending</span>}
                                </td>
                                <td className="px-4 py-3">
                                    {r.category ? (
                                        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border rounded ${_CAT_COLOR[r.category] || ""}`}>
                                            {_CAT_LABELS[r.category] || r.category}
                                        </span>
                                    ) : <span className="text-xs text-muted-foreground">—</span>}
                                </td>
                                <td className="px-4 py-3 text-right mono">{r.score ?? "—"}</td>
                                <td className="px-4 py-3 text-right">
                                    {r.completed && r.strategy_id ? (
                                        <button
                                            onClick={() => downloadPdf(r.strategy_id, r.email)}
                                            className="text-xs font-bold text-primary hover:underline"
                                            data-testid={`ac-pdf-btn-${r.user_id}`}
                                        >View PDF</button>
                                    ) : <span className="text-xs text-muted-foreground">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
